import { can, canAnywhere, type Member, MODULES, REQUIREMENTS } from '@epc/contracts';
import * as RD from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useMe } from '../lib/session';
import { useDevBoardPaletteItems } from '../pages/devboard/access';

interface Item {
  key: string;
  title: string;
  hint: string;
  to: string;
  /** Право, без которого пункт не показывается (поток 1, 26.10.2026) */
  perm?: string;
}

const EXTRA: Item[] = [
  { key: 'support-new', title: 'Написать в поддержку', hint: 'Поддержка, помощь, обращение', to: '/support' },
  {
    key: 'support-suggest',
    title: 'Предложения по порталу',
    hint: 'Поддержка, голосование',
    to: '/support?tab=suggestions',
  },
  {
    key: 'support-rewards',
    title: 'Премии за предложения и активность',
    hint: 'Поддержка, баллы, итоги месяца',
    to: '/support?tab=rewards',
  },
  {
    key: 'support-analysis',
    title: 'Анализ предложений',
    hint: 'Поддержка, что делать первым, повторы',
    to: '/support?tab=analysis',
  },
  {
    key: 'admin-members',
    title: 'Сотрудники и приглашения',
    hint: 'Администрирование',
    to: '/admin/members',
  },
  {
    key: 'admin-delegations',
    title: 'Замещения и временный доступ',
    hint: 'Администрирование',
    to: '/admin/delegations',
  },
  { key: 'admin-roles', title: 'Роли и права', hint: 'Администрирование', to: '/admin/roles' },
  { key: 'admin-le', title: 'Юрлица группы', hint: 'Администрирование', to: '/admin/legal-entities' },
  {
    key: 'admin-audit',
    title: 'Журнал изменений: отмена, восстановление, проверка базы',
    hint: 'Администрирование, история изменений',
    to: '/admin/audit',
  },
  {
    key: 'admin-audit-deleted',
    title: 'Восстановить удалённое',
    hint: 'Администрирование, журнал изменений',
    to: '/admin/audit?tab=deleted',
  },
  { key: 'admin-settings', title: 'Настройки компании', hint: 'Администрирование', to: '/admin/settings' },
  {
    key: 'admin-license',
    title: 'Лицензия и тариф компании',
    hint: 'Администрирование',
    to: '/admin/license',
  },
  {
    key: 'admin-backups',
    title: 'Резервные копии компании: куда, как часто, сколько хранить',
    hint: 'Администрирование',
    to: '/admin/backups',
  },
  {
    key: 'admin-integrations',
    title: 'Обмен с 1С и внешними системами',
    hint: 'Администрирование',
    to: '/admin/integrations',
  },
  {
    key: 'admin-status',
    title: 'Состояние системы: каналы, копии, мониторинг',
    hint: 'Администрирование',
    to: '/admin/status',
  },
  { key: 'profile', title: 'Профиль и пароль', hint: 'Личные настройки', to: '/profile' },
  {
    key: 'supplier-cabinet',
    title: 'Кабинет поставщика: ответы на запросы КП',
    hint: 'Закупки, поставщик, КП',
    to: '/suppliers/cabinet',
  },
  { key: 'wh-documents', title: 'Документы склада', hint: 'Склад', to: '/warehouse?tab=documents' },
  { key: 'wh-items', title: 'Каталог ТМЦ', hint: 'Склад', to: '/warehouse?tab=items' },
  { key: 'wh-warehouses', title: 'Склады', hint: 'Склад', to: '/warehouse?tab=warehouses' },
  {
    key: 'site-my-day',
    title: 'Мой день: план работ, поставки, наряды, замечания',
    hint: 'Площадка',
    to: '/site/day',
  },
  {
    key: 'site-missing',
    title: 'Площадка: кто не сдал отчёт смены',
    hint: 'Площадка',
    to: '/site/missing',
  },
  {
    key: 'site-summary',
    title: 'Площадка: сводка дня по проекту',
    hint: 'Площадка',
    to: '/site/summary',
  },
  {
    key: 'site-extra-works',
    title: 'Площадка: акты на дополнительные работы',
    hint: 'Площадка',
    to: '/site/extra-works',
  },
  {
    key: 'exec-docs',
    title: 'Исполнительная документация: перечень и готовность',
    hint: 'Документы',
    to: '/documents/exec',
  },
  { key: 'work-journal', title: 'Общий журнал работ', hint: 'Документы', to: '/documents/journals' },
  { key: 'letters', title: 'Письма: входящие и исходящие', hint: 'Документы', to: '/documents/letters' },
  { key: 'qa-remarks', title: 'Замечания и предписания ОТК, ОТ и ПБ', hint: 'Качество и ОТ', to: '/qhse' },
  {
    key: 'qa-inspections',
    title: 'Приёмки по чек-листам',
    hint: 'Качество и ОТ',
    to: '/qhse?tab=inspections',
  },
  { key: 'video-cameras', title: 'Камеры видеонаблюдения', hint: 'Видеонаблюдение', to: '/video' },
  { key: 'video-events', title: 'События видеоаналитики', hint: 'Видеонаблюдение', to: '/video?tab=events' },
  {
    key: 'video-legal',
    title: 'Положение о видеонаблюдении и согласия',
    hint: 'Видеонаблюдение',
    to: '/video?tab=legal',
  },
  { key: 'qa-templates', title: 'Шаблоны чек-листов', hint: 'Качество и ОТ', to: '/qhse?tab=templates' },
  { key: 'qa-permits', title: 'Наряды-допуски', hint: 'Качество и ОТ', to: '/qhse?tab=permits' },
  {
    key: 'qa-briefings',
    title: 'Инструктажи и журнал инструктажей',
    hint: 'Качество и ОТ',
    to: '/qhse?tab=briefings',
  },
  {
    key: 'qa-credentials',
    title: 'Допуски и удостоверения работников',
    hint: 'Качество и ОТ',
    to: '/qhse?tab=credentials',
  },
  {
    key: 'qa-observations',
    title: 'Наблюдения за безопасностью',
    hint: 'Качество и ОТ',
    to: '/qhse?tab=observations',
  },
  { key: 'qa-observe', title: 'Сообщить об опасности', hint: 'Качество и ОТ', to: '/qhse/observe' },
  { key: 'fl-park', title: 'Парк техники и документы со сроками', hint: 'Техника', to: '/fleet' },
  { key: 'fl-waybills', title: 'Наряды и путевые листы', hint: 'Техника', to: '/fleet?tab=waybills' },
  { key: 'fl-timesheet', title: 'Табель техники и загрузка', hint: 'Техника', to: '/fleet?tab=timesheet' },
  { key: 'fl-fuel', title: 'Топливо против нормы', hint: 'Техника', to: '/fleet?tab=fuel' },
  { key: 'fl-cost', title: 'Стоимость машино-часа', hint: 'Техника', to: '/fleet?tab=cost' },
  { key: 'fl-output', title: 'Выработка по работам', hint: 'Техника', to: '/fleet?tab=output' },
  {
    key: 'an-bridge',
    title: 'Мост маржи: от плана к прогнозу по причинам',
    hint: 'Руководителю',
    to: '/analytics?tab=bridge',
  },
  {
    key: 'an-sections',
    title: 'Отклонения по разделам: срок, себестоимость, причина',
    hint: 'Руководителю',
    to: '/analytics?tab=sections',
  },
  {
    key: 'an-ev',
    title: 'Освоенный объём проекта: PV, EV, AC, SPI, CPI',
    hint: 'Руководителю',
    to: '/analytics?tab=ev',
  },
  {
    key: 'rp-ready',
    title: 'Отчёты: еженедельный заказчику, план-факт, сводка руководителю',
    hint: 'Отчёты',
    to: '/reports',
  },
  {
    key: 'rp-builder',
    title: 'Конструктор отчётов',
    hint: 'Отчёты',
    to: '/reports/builder',
    perm: 'analytics.report.edit',
  },
  {
    key: 'rp-lists',
    title: 'Настраиваемые списки: колонки, отбор, выгрузка в Excel и PDF',
    hint: 'Отчёты',
    to: '/reports/lists',
    perm: 'lists.view',
  },
  { key: 'rp-digest', title: 'Сводка руководителю в 08:00', hint: 'Отчёты', to: '/reports?tab=digest' },
  {
    key: 'kb-articles',
    title: 'База знаний: статьи и регламенты',
    hint: 'База знаний',
    to: '/kb',
    perm: 'kb.view',
  },
  {
    key: 'kb-new',
    title: 'Написать статью в базу знаний',
    hint: 'База знаний',
    to: '/kb/new',
    perm: 'kb.edit',
  },
  {
    key: 'kb-subs',
    title: 'Подписки базы знаний',
    hint: 'База знаний',
    to: '/kb?tab=subscriptions',
    perm: 'kb.view',
  },
  { key: 'pl-schedules', title: 'Графики проектов', hint: 'График', to: '/schedule' },
  { key: 'rs-load', title: 'Загрузка людей и техники', hint: 'Ресурсы', to: '/resources?tab=load' },
  { key: 'rs-people', title: 'Персонал и допуски', hint: 'Ресурсы', to: '/resources?tab=people' },
  { key: 'rs-rotations', title: 'Вахты', hint: 'Ресурсы', to: '/resources?tab=rotations' },
  { key: 'rs-timesheet', title: 'Табель', hint: 'Ресурсы', to: '/resources?tab=timesheet' },
  { key: 'rs-dict', title: 'Справочник ресурсов и ставки', hint: 'Ресурсы', to: '/resources?tab=dict' },
  {
    key: 'norms-reviews',
    title: 'Нормативная библиотека: на проверку',
    hint: 'Нормативная база',
    to: '/norms/library/reviews',
  },
  { key: 'psd-search', title: 'Поиск по ПСД: шифр, лист, текст листа', hint: 'ПСД', to: '/psd' },
  {
    key: 'contractor-schedule',
    title: 'Мой график (кабинет подрядчика)',
    hint: 'Работы вашей организации в графиках проектов',
    to: '/schedule/contractor',
  },
  {
    key: 'fin-registry',
    title: 'Реестр платежей на утверждение',
    hint: 'Финансы, оплаты, утвердить, клиент-банк, 1С',
    to: '/finance?tab=registry',
    perm: 'finance.view',
  },
  {
    key: 'start',
    title: 'С чего начать: мои первые шаги и порядок работы на каждый день',
    hint: 'Инструкция по моим правам, помощь',
    to: '/start',
  },
];

/**
 * Быстрый переход: разделы портала и требования ТЗ. По мере появления модулей
 * сюда добавляется поиск по заявкам, договорам, поставщикам и листам ПСД.
 */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const me = useMe();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  // Карточки пользователей — администратору компании: поиск по имени и почте
  const canPeople = can(me.access, 'admin.users') && !me.viewAs;
  const members = useQuery({
    queryKey: ['admin', 'members'],
    queryFn: () => api<Member[]>('/admin/members'),
    enabled: open && canPeople,
    staleTime: 60_000,
  });
  // Доска задач разработки — тем, кому она открыта (владелец портала и люди, которых он добавил)
  const devboard = useDevBoardPaletteItems(open);

  const all = useMemo<Item[]>(() => {
    const modules = MODULES.filter((m) => canAnywhere(me.access, m.permission)).map((m) => ({
      key: m.code,
      title: m.title,
      hint: m.description,
      to: m.path,
    }));
    // Требования ТЗ и ссылки на «Ход разработки» демо-компании не показываем
    const reqs = (me.tenant?.isDemo ? [] : REQUIREMENTS).map((r) => {
      const m = MODULES.find((x) => x.code === r.module);
      return {
        key: r.code,
        title: `${r.code} ${r.text}`,
        hint: `Требование ТЗ, раздел ${r.section}`,
        to: m?.path ?? '/roadmap',
      };
    });
    // Кабинет владельца портала — только тем, у кого есть этот признак
    const platform: Item[] = me.user.isPlatformAdmin
      ? [
          {
            key: 'platform',
            title: 'Владелец портала: компании и лицензии',
            hint: 'Кабинет владельца портала',
            to: '/platform',
          },
          {
            key: 'platform-plans',
            title: 'Тарифы портала',
            hint: 'Кабинет владельца портала',
            to: '/platform/plans',
          },
          {
            key: 'platform-requests',
            title: 'Заявки на доступ',
            hint: 'Кабинет владельца портала',
            to: '/platform/requests',
          },
          {
            key: 'platform-invoices',
            title: 'Счета и оплаты лицензий',
            hint: 'Кабинет владельца портала',
            to: '/platform/invoices',
          },
          {
            key: 'platform-markets',
            title: 'Страны, налоги, правовые документы',
            hint: 'Кабинет владельца портала',
            to: '/platform/markets',
          },
          {
            key: 'platform-sales',
            title: 'Продажи: воронка, партнёры, панель по странам',
            hint: 'Кабинет владельца портала',
            to: '/platform/sales',
          },
          {
            key: 'platform-versions',
            title: 'Версии портала: откат и возврат обновлений, копия базы',
            hint: 'Кабинет владельца портала',
            to: '/platform/versions',
          },
        ]
      : [];
    const extra = EXTRA.filter((i) => !i.perm || canAnywhere(me.access, i.perm));
    // Супер-админка (ЛЦ-09…ЛЦ-13): люди, безопасность, объявления — всей команде портала, команда — владельцу
    const consoleItems: Item[] =
      (me.user.platformRole || me.user.isPlatformAdmin) && !me.viewAs
        ? [
            ...(me.user.isPlatformAdmin
              ? []
              : [
                  {
                    key: 'platform',
                    title: 'Администрирование портала: все компании',
                    hint: 'Кабинет портала',
                    to: '/platform',
                  },
                ]),
            {
              key: 'platform-users',
              title: 'Люди всех компаний',
              hint: 'Кабинет портала',
              to: '/platform/users',
            },
            {
              key: 'platform-security',
              title: 'Безопасность входа: журнал и блокировки',
              hint: 'Кабинет портала',
              to: '/platform/security',
            },
            {
              key: 'platform-announcements',
              title: 'Объявления компаниям',
              hint: 'Кабинет портала',
              to: '/platform/announcements',
            },
            ...(me.user.isPlatformAdmin
              ? [
                  {
                    key: 'platform-team',
                    title: 'Команда портала',
                    hint: 'Кабинет портала',
                    to: '/platform/team',
                  },
                ]
              : []),
          ]
        : [];
    const people: Item[] = canPeople
      ? (members.data ?? []).map((m) => ({
          key: `member-${m.membershipId}`,
          title: m.fullName,
          hint: `Карточка пользователя, ${m.email}`,
          to: `/admin/members/${m.membershipId}`,
        }))
      : [];
    return [...modules, ...extra, ...platform, ...consoleItems, ...devboard, ...people, ...reqs];
  }, [
    me.access,
    me.user.isPlatformAdmin,
    me.user.platformRole,
    me.viewAs,
    me.tenant?.isDemo,
    canPeople,
    members.data,
    devboard,
  ]);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return all.slice(0, 12);
    return all
      .filter((i) => i.title.toLowerCase().includes(s) || i.hint.toLowerCase().includes(s))
      .slice(0, 30);
  }, [q, all]);

  function go(item: Item) {
    onOpenChange(false);
    setQ('');
    navigate({ to: item.to });
  }

  return (
    <RD.Root open={open} onOpenChange={onOpenChange}>
      <RD.Portal>
        <RD.Overlay className="fixed inset-0 z-50 bg-ink/40" />
        <RD.Content className="fixed top-[10dvh] left-1/2 z-50 w-[min(640px,calc(100vw-24px))] -translate-x-1/2 overflow-hidden rounded-lg border border-line bg-surface shadow-md">
          <RD.Title className="sr-only">Поиск по порталу</RD.Title>
          <RD.Description className="sr-only">Введите название раздела или код требования</RD.Description>
          <div className="flex items-center gap-2 border-b border-line px-4">
            <Search className="size-4 text-ink-2" aria-hidden />
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setCursor(0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setCursor((c) => Math.min(c + 1, results.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setCursor((c) => Math.max(c - 1, 0));
                } else if (e.key === 'Enter' && results[cursor]) {
                  go(results[cursor]);
                }
              }}
              placeholder="Раздел, требование ТЗ (например, ЗК-05)"
              className="h-12 flex-1 bg-transparent text-[15px] text-ink outline-none placeholder:text-ink-3"
            />
          </div>
          <ul className="max-h-[60dvh] overflow-y-auto p-1.5">
            {results.length === 0 ? (
              <li className="px-3 py-6 text-[14px] text-ink-2">
                Ничего не найдено. Поиск по записям появится вместе с модулями.
              </li>
            ) : (
              results.map((r, i) => (
                <li key={r.key}>
                  <button
                    type="button"
                    onMouseEnter={() => setCursor(i)}
                    onClick={() => go(r)}
                    className={clsx(
                      'flex w-full flex-col items-start rounded-sm px-3 py-2 text-left',
                      i === cursor ? 'bg-surface-2' : '',
                    )}
                  >
                    <span className="line-clamp-1 text-[14px] text-ink">{r.title}</span>
                    <span className="line-clamp-1 text-[12px] text-ink-2">{r.hint}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}
